import { useState, useMemo } from 'react';
import { useGetDealerRegistrationListQuery, useGetDealerFilterListQuery, useGetDealerRegistrationStatusFilterListQuery, useGetDealerRegistrationCountryFilterListQuery } from '../../../../../api/endpoints/dealerRegistration';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const useDealerRegistrationData = () => {
    const { getCurrentLanguageId, th } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [pagination, setPagination] = useState({ first: 0, rows: 10, page: 1 });
    const [includeRejected, setIncludeRejected] = useState(false);
    // Bayi filtresi: dropdown'dan seçilen sapDealerId (null => tümü).
    const [sapDealerId, setSapDealerId] = useState(undefined);
    // Durum multi-select: overallStatuses (boş => tümü).
    const [overallStatuses, setOverallStatuses] = useState([]);
    // Ülke filtresi: dealer_address_t.country (null/undefined => tümü).
    const [country, setCountry] = useState(undefined);

    // Filtre dropdown'ı veri kaynağı: yalnızca kaydı olan bayiler (tekilleştirilmiş).
    const { data: dealerFilterList = [] } = useGetDealerFilterListQuery({ rowCount: 200 });
    const dealerOptions = useMemo(() => (dealerFilterList || []).map((d) => ({ label: d.name, value: d.sapDealerId })), [dealerFilterList]);

    // Durum filtresi: backend yalnızca { code, key } döner; etiket FE i18n'den üretilir.
    const { data: statusFilterList = [] } = useGetDealerRegistrationStatusFilterListQuery();
    const { data: countryFilterList = [] } = useGetDealerRegistrationCountryFilterListQuery();
    const countryOptions = useMemo(
        () =>
            (countryFilterList || [])
                .filter((item) => item?.country)
                .map((item) => ({ label: item.country, value: item.country })),
        [countryFilterList]
    );
    const statusOptions = useMemo(
        () =>
            (statusFilterList || []).map((s) => ({
                label: th(`dealerApproval.status.${s.code}`),
                value: s.code
            })),
        [statusFilterList, th]
    );

    const queryParams = useMemo(
        () => ({
            PageNumber: pagination.page,
            PageSize: pagination.rows,
            includeRejected,
            sapDealerId,
            overallStatuses,
            country,
            languageId
        }),
        [pagination.page, pagination.rows, includeRejected, sapDealerId, overallStatuses, country, languageId]
    );

    const { data, isLoading, isFetching, refetch } = useGetDealerRegistrationListQuery(queryParams);

    const onPageChange = (event) => {
        setPagination({ first: event.first, rows: event.rows, page: (event.page ?? 0) + 1 });
    };

    // GenericTableFilter alan tanımı (bayi dropdown + durum multi-select).
    const filterFields = useMemo(
        () => [
            {
                field: 'sapDealerId',
                headerName: th('dealerRegistration.filter.dealer'),
                type: 'dropdown',
                options: dealerOptions
            },
            {
                field: 'overallStatuses',
                headerName: th('dealerRegistration.filter.status'),
                type: 'multiselect',
                options: statusOptions
            },
            {
                field: 'country',
                headerName: th('dealerRegistration.filter.country'),
                type: 'dropdown',
                options: countryOptions,
                placeholder: th('dealerRegistration.filter.countryPlaceholder')
            }
        ],
        [th, dealerOptions, statusOptions, countryOptions]
    );

    // Uygula: seçilen bayiyi ve durumları filtreye al, sayfayı başa sar.
    const handleApplyFilters = (filters) => {
        setSapDealerId(filters?.sapDealerId || undefined);
        setOverallStatuses(Array.isArray(filters?.overallStatuses) ? filters.overallStatuses : []);
        setCountry(filters?.country || undefined);
        setPagination((p) => ({ ...p, first: 0, page: 1 }));
    };

    // Sıfırla: bayi, durum ve ülke filtrelerini temizle, sayfayı başa sar.
    const handleResetFilters = () => {
        setSapDealerId(undefined);
        setOverallStatuses([]);
        setCountry(undefined);
        setPagination((p) => ({ ...p, first: 0, page: 1 }));
    };

    return {
        items: data?.items || [],
        totalCount: data?.totalCount || 0,
        loading: isLoading || isFetching,
        pagination,
        onPageChange,
        includeRejected,
        setIncludeRejected,
        filterFields,
        handleApplyFilters,
        handleResetFilters,
        refetch
    };
};

export default useDealerRegistrationData;
